import React, { useState, useEffect } from "react";

import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';

/**
 * DataMasking component showcasing data masking feature
 * Displays information about data masking with an interactive chat demo
 * 
 * @returns {JSX.Element} The rendered DataMasking component
 */
const DataMasking: React.FC = () => {
  try {
    // Log component rendering
    console.log("Rendering DataMasking component");

    // User input and message states
    const [inputText, setInputText] = useState("");
    const [messages, setMessages] = useState<Array<{type: string, text: string, status?: string}>>([]);
    
    // Animation states
    const [isLoading, setIsLoading] = useState(false);
    const [showRaiduIntervention, setShowRaiduIntervention] = useState(false);
    const [raiduInterventionText, setRaiduInterventionText] = useState("");
    const [demoStarted, setDemoStarted] = useState(false);
    const [autoTypingActive, setAutoTypingActive] = useState(false);
    const [autoTypingIndex, setAutoTypingIndex] = useState(0);
    
    // Log animation state for debugging
    useEffect(() => {
      try {
        console.log("Demo started:", demoStarted);
        console.log("Auto typing active:", autoTypingActive);
      } catch (error) {
        console.error("Error logging animation state:", error);
      }
    }, [demoStarted, autoTypingActive]);
    
    // Store original and masked prompts in variables for easy modification
    const userPrompt = "My credit card number is 4111-1111-1111-1111 and my SSN is 123-45-6789.";
    const maskedPrompt = "My credit card number is [MASKED-CREDIT-CARD] and my SSN is [MASKED-SSN].";

    // Raidu intervention messages
    const interventionMessages = [
      "Sensitive data detected",
      "Masking in progress...",
      "Redirecting to secure channel..."
    ];

    // Function to handle sending a message
    const handleSendMessage = () => {
      try {
        if (demoStarted) return; // Prevent multiple sends
        
        setDemoStarted(true);
        
        // Log message send
        console.log("User message sent:", inputText || userPrompt);
        
        // Add user message to chat
        const messageToSend = inputText || userPrompt;
        setMessages(prev => [...prev, { type: 'user', text: messageToSend }]);
        setInputText("");
        
        // Show Raidu intervention
        setShowRaiduIntervention(true);
        
        // Animate Raidu intervention messages
        let messageIndex = 0;
        const interventionInterval = setInterval(() => {
          if (messageIndex < interventionMessages.length) {
            setRaiduInterventionText(interventionMessages[messageIndex]);
            if (messageIndex === 0) {
              // Add first intervention message to chat
              setMessages(prev => [...prev, { 
                type: 'raidu', 
                text: interventionMessages[messageIndex],
                status: 'intervention'
              }]);
            }
            messageIndex++;
          } else {
            clearInterval(interventionInterval);
            
            // After intervention, show loading state
            setIsLoading(true);
            
            // After loading, show masked response
            setTimeout(() => {
              setIsLoading(false);
              setShowRaiduIntervention(false);
              
              // Add masked message to chat
              setMessages(prev => [...prev, { 
                type: 'ai', 
                text: maskedPrompt,
                status: 'masked'
              }]);
              
              // Animation is now complete - log for debugging
              console.log("Animation sequence completed");
            }, 1500);
          }
        }, 800);
      } catch (error) {
        // Log any errors during message sending
        console.error("Error sending message:", error);
      }
    };
    
    // Start auto-typing animation when component mounts without auto-focusing the input field
    useEffect(() => {
      try {
        // Log component initialization
        console.log("DataMasking demo initialization");
        
        // Start auto-typing animation after a short delay
        const autoTypeTimer = setTimeout(() => {
          if (!demoStarted && !autoTypingActive) {
            setAutoTypingActive(true);
          }
        }, 1000);
        
        return () => clearTimeout(autoTypeTimer);
      } catch (error) {
        console.error("Error initializing DataMasking demo:", error);
      }
    }, []);
    
    // Handle auto-typing animation and auto-click send button
    useEffect(() => {
      if (autoTypingActive && autoTypingIndex < userPrompt.length) {
        const typingTimer = setTimeout(() => {
          try {
            setInputText(userPrompt.substring(0, autoTypingIndex + 1));
            setAutoTypingIndex(prev => prev + 1);
            
            // Ensure cursor is visible during auto-typing
            const inputField = document.getElementById('chat-input');
            if (inputField) {
              // Use setTimeout to ensure DOM updates before scrolling
              setTimeout(() => {
                inputField.scrollLeft = inputField.scrollWidth;
              }, 0);
            }
          } catch (error) {
            console.error("Error during auto-typing:", error);
          }
        }, 50);
        
        return () => clearTimeout(typingTimer);
      } else if (autoTypingActive && autoTypingIndex >= userPrompt.length) {
        setAutoTypingActive(false);
        
        // Auto-click send button after typing is complete
        setTimeout(() => {
          if (!demoStarted) {
            handleSendMessage();
          }
        }, 500);
      }
    }, [autoTypingActive, autoTypingIndex, userPrompt, demoStarted]);

    // Reset animation function
    const resetAnimation = () => {
      try {
        console.log("Resetting animation");
        setInputText("");
        setMessages([]);
        setIsLoading(false);
        setShowRaiduIntervention(false);
        setRaiduInterventionText("");
        setDemoStarted(false);
        setAutoTypingActive(false);
        setAutoTypingIndex(0);
        
        // Focus the input field after reset
        setTimeout(() => {
          const inputField = document.getElementById('chat-input');
          if (inputField) {
            inputField.focus();
          }
          
          // Start auto-typing again after a short delay
          setTimeout(() => {
            if (!demoStarted && !autoTypingActive) {
              setAutoTypingActive(true);
            }
          }, 500);
        }, 100);
      } catch (error) {
        // Log any errors during reset
        console.error("Error resetting animation:", error);
      }
    };

    return (
      <div id="data-masking" className="inset-0">
        {/* Subtle dot pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(#6366f1 0.75px, transparent 0.75px)",
            backgroundSize: "12px 12px",
          }}
        ></div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10 py-8 md:py-12">
          <div className="overflow-hidden">
            <div className="grid grid-cols-1 items-center gap-x-6 gap-y-8 lg:grid-cols-2 p-6 sm:p-8 lg:p-10">
              <div className="mx-auto w-full max-w-xl lg:mx-0">
                <div className="inline-block mb-4">
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent text-sm font-medium px-3 py-1 rounded-full bg-opacity-10 border border-indigo-200 bg-indigo-50">
                    Privacy Protection
                  </span>
                </div>
                <h2 className="text-4xl font-semibold tracking-tight bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent sm:text-5xl">
                  Intelligent Data Masking
                </h2>
                <p className="mt-6 text-lg text-gray-600">
                  Raidu's AI-powered data masking automatically detects and redacts sensitive information in prompts and responses, ensuring compliance with privacy regulations and protecting your organization's data.
                </p>
                <div className="mt-8 flex items-center gap-x-6">
                  <a
                    href="/booking"
                    className="rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-md hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={() => {
                      try {
                        // Log button click
                        console.log("DataMasking - Get Started button clicked");
                      } catch (error) {
                        // Log any errors during click handling
                        console.error(
                          "Error handling Get Started button click:",
                          error
                        );
                      }
                    }}
                  >
                    Get Started
                  </a>
                  <a
                    href="/booking"
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 transition-colors"
                    onClick={() => {
                      try {
                        // Log button click
                        console.log("DataMasking - Learn More button clicked");
                      } catch (error) {
                        // Log any errors during click handling
                        console.error(
                          "Error handling Learn More button click:",
                          error
                        );
                      }
                    }}
                  >
                    Learn More <span aria-hidden="true">&rarr;</span>
                  </a>
                </div>
              </div>
              
              {/* Chat window demo */}
              <div className="relative mx-auto w-full max-w-xl lg:mx-0 lg:max-w-none lg:pl-8">
                
                {/* Chat interface */}
                <div className="rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                  {/* Chat header */}
                  <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
                    <div className="flex items-center">
                      <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                      <div className="flex items-center ml-2">
                        <img src="/logos/raidu_logo.png" alt="Raidu" className="h-5 w-5 mr-2" />
                        <span className="text-gray-800 text-sm font-medium">Raidu Chat</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div 
                        onClick={resetAnimation}
                        className="relative flex cursor-pointer transition-transform hover:scale-110"
                        title="Reset Demo"
                      >
                        {/* OpenAI (ChatGPT) icon */}
                        <img 
                          src="/ai-icons/openai.svg" 
                          alt="ChatGPT" 
                          className="h-7 w-7 z-10" 
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Chat messages */}
                  <div className="h-80 overflow-y-auto p-4 bg-white" id="chat-container">
                    {messages.map((message, index) => (
                      <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} mb-4`}>
                        {message.type !== 'user' && (
                          <div className="flex-shrink-0 mr-2">
                            {message.type === 'raidu' ? (
                              <img src="/logos/raidu_logo.png" alt="Raidu" className="h-8 w-8 rounded-full" />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-xs font-bold">
                                AI
                              </div>
                            )}
                          </div>
                        )}
                        <div 
                          className={`rounded-lg px-4 py-2 ${message.type === 'user' 
                            ? 'bg-indigo-600 text-white max-w-[70%]' 
                            : message.type === 'raidu' 
                              ? 'bg-yellow-100 text-gray-800 border border-yellow-300 max-w-[70%]' 
                              : 'bg-gray-200 text-gray-800 max-w-[70%]'}`}
                          style={{ wordBreak: 'break-word' }}
                        >
                          {message.type === 'raidu' && (
                            <p className="text-xs font-medium text-indigo-700 mb-1">Raidu Security Layer</p>
                          )}
                          <p className="text-sm">{message.text}</p>
                          {message.status === 'masked' && (
                            <p className="text-xs text-green-600 mt-1">✓ Sensitive data masked by Raidu</p>
                          )}
                        </div>
                        {message.type === 'user' && (
                          <div className="flex-shrink-0 ml-2">
                            <div className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-xs font-bold">
                              U
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {/* Current Raidu intervention message (not yet added to messages array) */}
                    {showRaiduIntervention && raiduInterventionText && !messages.some(m => m.text === raiduInterventionText) && (
                      <div className="flex justify-start mb-4">
                        <div className="flex items-start">
                          <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mr-2">
                            <img src="/logos/raidu_logo.png" alt="Raidu" className="h-5 w-5" />
                          </div>
                          <div className="bg-indigo-50 border border-indigo-100 rounded-lg py-2 px-4 max-w-[80%]">
                            <p className="text-xs font-medium text-indigo-700 mb-1">Raidu Security Layer</p>
                            <p className="text-sm text-gray-800">
                              {raiduInterventionText}
                              <span className="inline-block w-2 h-4 bg-indigo-500 ml-1 animate-pulse"></span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Loading indicator */}
                    {isLoading && (
                      <div className="flex justify-start mb-4">
                        <div className="flex items-start">
                          <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mr-2">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11.5294 1.5C6.27908 1.5 2 5.77908 2 11.0294C2 16.2797 6.27908 20.5588 11.5294 20.5588C12.6378 20.5588 13.7053 20.3587 14.6967 20.0044L18.8811 22.4588C19.0515 22.5545 19.2493 22.5789 19.4371 22.5271C19.6248 22.4753 19.7869 22.3512 19.8862 22.1809C19.9855 22.0105 20.0135 21.8126 19.9651 21.6248C19.9167 21.437 19.7956 21.2748 19.6276 21.1748L15.4432 18.7204C17.3719 17.0135 18.5588 14.1762 18.5588 11.0294C18.5588 5.77908 14.2797 1.5 9.02941 1.5" stroke="#10A37F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                          <div className="bg-gray-100 rounded-lg py-2 px-4 max-w-[80%]">
                            <div className="flex items-center">
                              <div className="w-2 h-2 bg-gray-500 rounded-full mr-1 animate-bounce"></div>
                              <div className="w-2 h-2 bg-gray-500 rounded-full mr-1 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                              <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Chat input */}
                  <div className="border-t border-gray-200 p-3 bg-white">
                      <form 
                      className="relative" 
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!demoStarted && inputText) {
                          handleSendMessage();
                        }
                      }}
                    >
                      <input 
                        id="chat-input"
                        type="text" 
                        className="w-full rounded-full border border-gray-300 px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-ellipsis overflow-hidden"
                        placeholder="Type a message..."
                        value={inputText}
                        onChange={(e) => {
                          if (!demoStarted && !autoTypingActive) {
                            setInputText(e.target.value);
                          }
                        }}
                        disabled={demoStarted}
                        style={{ scrollBehavior: 'smooth' }}
                        onFocus={(e) => {
                          try {
                            // Ensure cursor is visible by scrolling to end
                            const input = e.target;
                            input.scrollLeft = input.scrollWidth;
                            console.log("Input focused, scrolled to cursor position");
                          } catch (error) {
                            console.error("Error handling input focus:", error);
                          }
                        }}
                      />
                      <button 
                        type="submit"
                        className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-1 rounded-full ${demoStarted ? 'text-gray-400 cursor-not-allowed' : 'text-indigo-600 hover:bg-indigo-50 cursor-pointer'}`}
                        disabled={demoStarted}
                        onClick={(e) => {
                          e.preventDefault();
                          if (!demoStarted) {
                            handleSendMessage();
                          }
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    // Log any errors during rendering
    console.error("Error rendering DataMasking component:", error);

    // Return error state
    return (
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-base font-semibold text-indigo-600">Error</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Failed to load data masking section
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Please refresh the page or try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }
};

export default DataMasking;
